'use client';

import { maskPhoneInput, normalizePhone } from '@invexa/shared';
import { ArrowRight, Check, Lock, Mail, Phone, User } from 'lucide-react';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { Suspense, useEffect, useState } from 'react';
import { AuthTabs, FormError, IconInput, PasswordInput } from '@/components/auth/fields';
import { Button } from '@/components/ui/button';
import { ApiError, errorMessage, post } from '@/lib/api';
import { cn } from '@/lib/utils';

const REF_KEY = 'invexa_ref';
const REF_TTL_MS = 30 * 86_400_000;

/** The referral code survives for 30 days in this browser, so a visitor who comes back later is still attributed. */
function storedRef(): string {
  try {
    const raw = JSON.parse(localStorage.getItem(REF_KEY) ?? 'null') as { code: string; at: number } | null;
    return raw && Date.now() - raw.at < REF_TTL_MS ? raw.code : '';
  } catch {
    return '';
  }
}

function RegisterForm() {
  const router = useRouter();
  const params = useSearchParams();
  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '', referralCode: params.get('ref') ?? '' });
  useEffect(() => {
    const ref = params.get('ref')?.trim().toUpperCase();
    if (!ref) {
      const saved = storedRef();
      if (saved) setForm((f) => (f.referralCode ? f : { ...f, referralCode: saved }));
      return;
    }
    try {
      localStorage.setItem(REF_KEY, JSON.stringify({ code: ref, at: Date.now() }));
    } catch {
      /* storage unavailable */
    }
    void post('/affiliate/visit', { code: ref }).catch(() => undefined);
  }, [params]);
  const [accept, setAccept] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const strong = form.password.length >= 8;
  const [confirm, setConfirm] = useState('');
  const matches = confirm.length > 0 && confirm === form.password;
  const phoneOk = normalizePhone(form.phone) !== null;
  const [phoneTouched, setPhoneTouched] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (form.password !== confirm) {
      setError('As senhas não conferem.');
      return;
    }
    if (!phoneOk) {
      setPhoneTouched(true);
      setError('Informe um telefone válido com DDD.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await post('/auth/register', { ...form, referralCode: form.referralCode || undefined, acceptTerms: accept });
      router.replace('/conectar');
      router.refresh();
    } catch (err) {
      setError(err instanceof ApiError && err.code === 'VALIDATION' ? 'Confira os campos e aceite os termos.' : errorMessage(err));
      setLoading(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-4">
      <AuthTabs active="register" />
      <div className="mb-6">
        <h2 className="text-2xl font-semibold tracking-tight">Crie sua conta</h2>
        <p className="mt-1 text-sm text-fg-2">Leva menos de um minuto. Depois é só conectar sua corretora.</p>
      </div>
      <IconInput icon={User} label="Nome" name="name" required autoComplete="name" autoFocus value={form.name} onChange={set('name')} placeholder="Seu nome" />
      <IconInput icon={Mail} label="E-mail" name="email" type="email" inputMode="email" required autoComplete="email" value={form.email} onChange={set('email')} placeholder="voce@email.com" />
      <IconInput
        icon={Phone}
        label="Telefone / WhatsApp"
        name="phone"
        type="tel"
        inputMode="tel"
        required
        autoComplete="tel"
        value={form.phone}
        onChange={(e) => setForm((f) => ({ ...f, phone: maskPhoneInput(e.target.value) }))}
        onBlur={() => setPhoneTouched(true)}
        placeholder="(11) 99999-9999"
        hint={
          phoneTouched && form.phone && !phoneOk ? (
            <span className="text-loss">Confira o número: DDD + telefone. De fora do Brasil, comece com + e o código do país.</span>
          ) : (
            <span className={cn('inline-flex items-center gap-1 transition-colors', phoneOk && 'text-win')}>{phoneOk && <Check className="size-3" />}Com DDD. Usado só para contato e suporte.</span>
          )
        }
      />
      <PasswordInput
        icon={Lock}
        label="Senha"
        name="password"
        required
        autoComplete="new-password"
        minLength={8}
        value={form.password}
        onChange={set('password')}
        placeholder="Crie uma senha"
        hint={<span className={cn('inline-flex items-center gap-1 transition-colors', strong && 'text-win')}>{strong && <Check className="size-3" />}Mínimo de 8 caracteres</span>}
      />
      <PasswordInput
        icon={Lock}
        label="Confirmar senha"
        name="confirmPassword"
        required
        autoComplete="new-password"
        value={confirm}
        onChange={(e) => setConfirm(e.target.value)}
        placeholder="Repita a senha"
        aria-invalid={confirm.length > 0 && !matches}
        hint={
          confirm.length === 0 ? undefined : matches ? (
            <span className="inline-flex items-center gap-1 text-win">
              <Check className="size-3" /> As senhas conferem
            </span>
          ) : (
            <span className="text-loss">As senhas não conferem</span>
          )
        }
      />
      <label className="flex cursor-pointer items-start gap-2.5 rounded-xl border border-border-soft bg-bg-2 p-3 transition-colors hover:border-border text-xs leading-relaxed text-fg-2">
        <input type="checkbox" checked={accept} onChange={(e) => setAccept(e.target.checked)} className="mt-0.5 size-4 accent-[#C7F23A]" />
        <span>
          Li e aceito os{' '}
          <Link href="/termos" target="_blank" className="text-lime hover:underline">
            Termos de Uso e o Aviso de Risco
          </Link>
          . Entendo que operações com opções digitais podem resultar na perda total do valor investido.
        </span>
      </label>
      {error && <FormError>{error}</FormError>}
      <Button type="submit" variant="primary" size="lg" className="h-12 w-full rounded-xl text-[15px]" loading={loading} disabled={!accept || !matches}>
        Criar conta <ArrowRight />
      </Button>
      <p className="pt-2 text-center text-sm text-fg-3">
        Já tem conta?{' '}
        <Link href="/login" className="font-medium text-lime hover:underline">
          Entrar
        </Link>
      </p>
    </form>
  );
}

export default function RegisterPage() {
  return (
    <Suspense>
      <RegisterForm />
    </Suspense>
  );
}
